import { PlatformMapLayerGroup } from './types'
import * as Cesium from 'cesium'

export interface BaseMapPrewarmOptions {
  levels?: number[]
  radius?: number
  radiusByLevel?: Record<number, number>
  concurrency?: number
  includeOverlays?: boolean
}

function setGroupVisible (group: PlatformMapLayerGroup, visible: boolean) {
  group.visible = visible
  group.layers.forEach(layer => {
    if (layer && 'show' in layer && layer.show !== visible) layer.show = visible
  })
}

function hasSameLayers (left?: PlatformMapLayerGroup, right?: PlatformMapLayerGroup) {
  if (!left || !right || left.layers.length !== right.layers.length) return false
  return left.layers.every((layer, index) => layer === right.layers[index])
}

export class BaseMapLayerManager {
  private groups = new Map<string, PlatformMapLayerGroup>()
  private activeGroupId = ''
  private warmedTiles = new WeakMap<object, Set<string>>()
  private warmingTiles = new WeakMap<object, Map<string, Promise<boolean>>>()

  register (group: PlatformMapLayerGroup) {
    const previous = this.groups.get(group.id)
    const reusesExistingLayers = hasSameLayers(previous, group)
    if (previous && previous !== group && !reusesExistingLayers) {
      setGroupVisible(previous, false)
    }
    this.groups.set(group.id, group)
    if (reusesExistingLayers) {
      group.visible = previous?.visible
      return
    }
    setGroupVisible(group, group.id === this.activeGroupId)
  }

  activate (groupId: string, viewer?: any) {
    const nextGroup = this.groups.get(groupId)
    if (!nextGroup) throw new Error(`底图图层组不存在: ${groupId}`)
    if (groupId === this.activeGroupId) return

    // Apply visibility in one synchronous task so base imagery and labels do
    // not expose an intermediate mismatched frame.
    this.groups.forEach((group, id) => setGroupVisible(group, id === groupId))
    this.activeGroupId = groupId
    viewer?.scene?.requestRender?.()
  }

  get activeId () {
    return this.activeGroupId
  }

  get activeLayers () {
    return this.groups.get(this.activeGroupId)?.layers || []
  }

  getGroup (groupId: string) {
    return this.groups.get(groupId)
  }

  async prewarmActiveImagery (
    center: { lng: number; lat: number },
    options: BaseMapPrewarmOptions = {}
  ) {
    const lng = Number(center?.lng)
    const lat = Number(center?.lat)
    if (!Number.isFinite(lng) || !Number.isFinite(lat) || lng < -180 || lng > 180 || lat < -90 || lat > 90) {
      return 0
    }

    const levels = Array.from(new Set(options.levels || [14, 15, 16]))
      .map(level => Math.max(0, Math.floor(Number(level))))
      .filter(Number.isFinite)
    const radius = Math.max(0, Math.min(4, Math.floor(Number(options.radius ?? 1))))
    const concurrency = Math.max(1, Math.min(16, Math.floor(Number(options.concurrency ?? 8))))
    const layersToWarm = options.includeOverlays === true
      ? this.activeLayers
      : this.activeLayers.slice(0, 1)
    const providers = Array.from(new Set(
      layersToWarm
        .map(layer => layer?.imageryProvider || layer?._imageryProvider)
        .filter(provider => provider && typeof provider.requestImage === 'function' && provider.tilingScheme)
    )) as any[]
    if (!providers.length || !levels.length) return 0

    const position = Cesium.Cartographic.fromDegrees(lng, lat)
    const jobs: Array<() => Promise<boolean>> = []
    providers.forEach(provider => {
      let warmed = this.warmedTiles.get(provider)
      if (!warmed) {
        warmed = new Set<string>()
        this.warmedTiles.set(provider, warmed)
      }
      let warming = this.warmingTiles.get(provider)
      if (!warming) {
        warming = new Map<string, Promise<boolean>>()
        this.warmingTiles.set(provider, warming)
      }

      levels.forEach(level => {
        const centerTile = provider.tilingScheme.positionToTileXY(position, level)
        if (!centerTile) return
        const levelRadius = Math.max(0, Math.min(
          4,
          Math.floor(Number(options.radiusByLevel?.[level] ?? radius))
        ))
        const xCount = Number(provider.tilingScheme.getNumberOfXTilesAtLevel(level))
        const yCount = Number(provider.tilingScheme.getNumberOfYTilesAtLevel(level))
        for (let offsetY = -levelRadius; offsetY <= levelRadius; offsetY += 1) {
          for (let offsetX = -levelRadius; offsetX <= levelRadius; offsetX += 1) {
            const x = ((Number(centerTile.x) + offsetX) % xCount + xCount) % xCount
            const y = Number(centerTile.y) + offsetY
            if (y < 0 || y >= yCount) continue
            const tileKey = `${level}/${x}/${y}`
            if (warmed?.has(tileKey)) continue
            jobs.push(() => {
              const activeRequest = warming?.get(tileKey)
              if (activeRequest) return activeRequest
              const request = Promise.resolve()
                .then(() => provider.requestImage(x, y, level))
                .then(image => {
                  if (!image) return false
                  warmed?.add(tileKey)
                  return true
                })
                .catch(() => false)
                .finally(() => warming?.delete(tileKey))
              warming?.set(tileKey, request)
              return request
            })
          }
        }
      })
    })

    let cursor = 0
    let completed = 0
    const workers = Array.from({ length: Math.min(concurrency, jobs.length) }, async () => {
      while (cursor < jobs.length) {
        const job = jobs[cursor]
        cursor += 1
        if (await job()) completed += 1
      }
    })
    await Promise.all(workers)
    return completed
  }

  clear () {
    this.groups.forEach(group => setGroupVisible(group, false))
    this.groups.clear()
    this.activeGroupId = ''
    this.warmedTiles = new WeakMap<object, Set<string>>()
    this.warmingTiles = new WeakMap<object, Map<string, Promise<boolean>>>()
  }
}
